import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Grid, Paper, Typography, Stack, Button, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Breadcrumbs, Dialog, DialogTitle,
    DialogContent, DialogActions, MenuItem, Divider,
    useTheme, useMediaQuery
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { getApiUrl } from '../config/api';

const TYPE_COLORS: any = {
    ASSET: { bg: '#EBF8FF', text: '#3182CE' },
    LIABILITY: { bg: '#FFF5F5', text: '#E53E3E' },
    EQUITY: { bg: '#FAF5FF', text: '#805AD5' },
    INCOME: { bg: '#E6FFFA', text: '#38A169' },
    EXPENSE: { bg: '#FFFAF0', text: '#DD6B20' },
};

const AccountsPage = () => {
    const [accounts, setAccounts] = useState<any[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<any>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState({
        code: '',
        name: '',
        type: 'ASSET'
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fetchAccounts = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl('accounts'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAccounts(res.data);
            if (res.data.length > 0 && !selectedAccount) {
                setSelectedAccount(res.data[0]);
            }
        } catch (error) {
            console.error('Fetch accounts error:', error);
        }
    }, [selectedAccount]);

    const fetchAccountDetail = async (id: string) => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl(`accounts/${id}`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedAccount(res.data);
        } catch (error) {
            console.error('Fetch account detail error:', error);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, [fetchAccounts]);

    const handleCreateAccount = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            await axios.post(getApiUrl('accounts'), formData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setOpenDialog(false);
            fetchAccounts();
        } catch (error) {
            console.error('Create account error:', error);
        }
    };

    return (
        <Box sx={{ p: isMobile ? 2 : 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={2} spacing={isMobile ? 1 : 0}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C">Chart of Accounts</Typography>
                    <Typography variant="body2" color="text.secondary">General ledger accounts and balances</Typography>
                </Box>
                {!isMobile && (
                    <Breadcrumbs separator="/" aria-label="breadcrumb">
                        <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>Accounts</Typography>
                        <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Chart of Accounts</Typography>
                    </Breadcrumbs>
                )}
            </Stack>

            <Stack direction={isSmallMobile ? "column" : "row"} justifyContent="flex-end" spacing={2} mb={3}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                    sx={{ bgcolor: '#263B7C', borderRadius: '8px', px: 3, width: isSmallMobile ? '100%' : 'auto' }}
                >
                    NEW ACCOUNT
                </Button>
            </Stack>

            <Grid container spacing={3}>
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', overflow: 'hidden' }}>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: isMobile ? 600 : 'auto' }}>
                                <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>CODE</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>ACCOUNT NAME</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>TYPE</TableCell>
                                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>BALANCE</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {accounts.map((acc) => (
                                        <TableRow
                                            key={acc.id}
                                            hover
                                            onClick={() => fetchAccountDetail(acc.id)}
                                            selected={selectedAccount?.id === acc.id}
                                            sx={{ cursor: 'pointer' }}
                                        >
                                            <TableCell sx={{ fontWeight: 600, color: '#263B7C' }}>{acc.code}</TableCell>
                                            <TableCell>{acc.name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={acc.type}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: TYPE_COLORS[acc.type]?.bg,
                                                        color: TYPE_COLORS[acc.type]?.text,
                                                        fontWeight: 700,
                                                        fontSize: '0.7rem'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>
                                                ${Number(acc.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                <Grid size={{ xs: 12, lg: 4 }}>
                    {selectedAccount ? (
                        <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', p: 3 }}>
                            <Typography variant="h6" fontWeight={700} mb={3}>Account History</Typography>
                            <Stack spacing={2}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">ACCOUNT</Typography>
                                    <Typography fontWeight={600} color="#263B7C">{selectedAccount.code} - {selectedAccount.name}</Typography>
                                </Box>
                                <Divider />
                                <Typography fontWeight={700} sx={{ mt: 2 }}>Recent Entries</Typography>
                                {selectedAccount.entries?.map((entry: any) => (
                                    <Box key={entry.id} sx={{ mb: 1, p: 1, borderBottom: '1px solid #f1f5f9' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                            {new Date(entry.postedAt).toLocaleDateString()}
                                        </Typography>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">{entry.description}</Typography>
                                            <Typography variant="body2" sx={{ color: entry.debit > 0 ? '#38A169' : '#E53E3E', fontWeight: 600 }}>
                                                {entry.debit > 0 ? `+${Number(entry.debit).toLocaleString()}` : `-${Number(entry.credit).toLocaleString()}`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                                {(!selectedAccount.entries || selectedAccount.entries.length === 0) && (
                                    <Typography variant="body2" color="text.secondary">No recent transactions</Typography>
                                )}
                            </Stack>
                        </Paper>
                    ) : (
                        <Box sx={{ p: 5, textAlign: 'center', opacity: 0.5 }}>
                            <AccountBalanceIcon sx={{ fontSize: 64, mb: 2 }} />
                            <Typography>Select an account to view history</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>

            {/* Create Account Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth fullScreen={isSmallMobile}>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white' }}>New General Ledger Account</DialogTitle>
                <DialogContent sx={{ p: isSmallMobile ? 2 : 4 }}>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <TextField
                            fullWidth label="Account Code (e.g., 4001)"
                            value={formData.code}
                            onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                        />
                        <TextField
                            fullWidth label="Account Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                        <TextField
                            fullWidth select label="Account Type"
                            value={formData.type}
                            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                        >
                            <MenuItem value="ASSET">Asset</MenuItem>
                            <MenuItem value="LIABILITY">Liability</MenuItem>
                            <MenuItem value="EQUITY">Equity</MenuItem>
                            <MenuItem value="INCOME">Income</MenuItem>
                            <MenuItem value="EXPENSE">Expense</MenuItem>
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateAccount} sx={{ bgcolor: '#F9B43F' }}>Create Account</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AccountsPage;
